import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import InlineTextInput from '@/components/ui/inline-text-input';
import { TaskableType } from '@/models/types.ts';

export type TaskDialogMode = 'one-time' | 'template';

const TaskDialogV2 = ({
  mode,
  taskableType,
  titleValue,
  setTitleValue,
  descriptionValue,
  setDescriptionValue,
  saveAsTemplate,
  setSaveAsTemplate,
  addToCurrentEntity,
  setAddToCurrentEntity,
  onClickCta,
  ctaText,
  onOpen,
  onClose,
  dialogIsOpen,
  children,
}: {
  mode: TaskDialogMode;
  taskableType: TaskableType;
  titleValue: string;
  setTitleValue: (titleValue: string) => void;
  descriptionValue: string;
  setDescriptionValue: (descriptionValue: string) => void;
  saveAsTemplate: boolean;
  setSaveAsTemplate: (saveAsTemplate: boolean) => void;
  addToCurrentEntity: boolean;
  setAddToCurrentEntity: (addToCurrentEntity: boolean) => void;
  onClickCta: () => void;
  ctaText: string;
  onOpen: () => void;
  onClose: () => void;
  dialogIsOpen: boolean;
  children?: React.ReactNode;
}) => {
  const dialogConfig = {
    'one-time': {
      title: 'Create One-Time Task',
      description: `Add a task just for this ${taskableType}.`,
    },
    template: {
      title: 'Create Task Template',
      description: 'Create a reusable task template.',
    },
  };

  return (
    <Dialog open={dialogIsOpen} modal={false}>
      <DialogTrigger asChild onClick={onOpen}>
        {children}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-[425px]"
        onClickClose={onClose}
        onPointerDownOutside={onClose}
      >
        <DialogHeader>
          <DialogTitle>{dialogConfig[mode].title}</DialogTitle>
          <DialogDescription>
            {dialogConfig[mode].description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <InlineTextInput
            className="rounded text-xs"
            placeholder="Add Title"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
          />
          <InlineTextInput
            className="rounded text-xs"
            placeholder="Add Description"
            value={descriptionValue}
            onChange={(e) => setDescriptionValue(e.target.value)}
          />

          {mode === 'one-time' && (
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="save-as-template"
                checked={saveAsTemplate}
                onCheckedChange={(checked) => setSaveAsTemplate(!!checked)}
              />
              <label
                htmlFor="save-as-template"
                className="text-xs text-gray-600 cursor-pointer select-none"
              >
                Save as template for reuse
              </label>
            </div>
          )}

          {mode === 'template' && (
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="add-to-entity"
                checked={addToCurrentEntity}
                onCheckedChange={(checked) => setAddToCurrentEntity(!!checked)}
              />
              <label
                htmlFor="add-to-entity"
                className="text-xs text-gray-600 cursor-pointer select-none"
              >
                Add to this {taskableType} now
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="submit"
            onClick={onClickCta}
            disabled={!titleValue.trim()}
          >
            {ctaText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDialogV2;
